import React, { useEffect, useState } from "react";
import { MdAccessTime, MdSave, MdAdd, MdDelete } from "react-icons/md";
import Switch from "components/switch";
import { useAuth } from "contexts/AuthContext";
import { getBusinessHours, updateBusinessHours } from "services/scheduleService";

const DAYS = [
  { key: "monday", label: "Lundi" },
  { key: "tuesday", label: "Mardi" },
  { key: "wednesday", label: "Mercredi" },
  { key: "thursday", label: "Jeudi" },
  { key: "friday", label: "Vendredi" },
  { key: "saturday", label: "Samedi" },
  { key: "sunday", label: "Dimanche" },
];

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const emptyPeriod = () => ({ open: "09:00", close: "22:00" });

const defaultDays = () =>
  DAYS.reduce((acc, d) => ({
    ...acc,
    [d.key]: { isOpen: true, periods: [emptyPeriod()] },
  }), {});

function TimeInput({ value, onChange }) {
  return (
    <input
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 px-2 py-1.5 text-sm text-navy-700 dark:text-white w-28"
    />
  );
}

function DayRow({ day, data, onChange, isToday }) {
  const addPeriod = () => onChange({ ...data, periods: [...data.periods, emptyPeriod()] });
  const removePeriod = (i) => onChange({ ...data, periods: data.periods.filter((_, idx) => idx !== i) });
  const updatePeriod = (i, field, val) =>
    onChange({ ...data, periods: data.periods.map((p, idx) => idx === i ? { ...p, [field]: val } : p) });

  return (
    <div className={`rounded-xl border p-4 transition-colors ${
      isToday ? "border-brand-400 bg-brand-50 dark:bg-brand-900/10" : "border-gray-100 dark:border-navy-700 bg-white dark:bg-navy-800"
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`font-bold text-sm ${isToday ? "text-brand-600" : "text-navy-700 dark:text-white"}`}>
            {day.label}
            {isToday && <span className="ml-2 text-xs bg-brand-500 text-white rounded-full px-2 py-0.5">Aujourd'hui</span>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {data.isOpen && (
            <span className="text-xs text-green-600 font-semibold">Ouvert</span>
          )}
          <Switch
            color={data.isOpen ? "green" : "gray"}
            checked={data.isOpen}
            onChange={() => onChange({ ...data, isOpen: !data.isOpen })}
          />
        </div>
      </div>

      {data.isOpen && (
        <div className="space-y-2">
          {data.periods.map((period, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap">
              <TimeInput value={period.open} onChange={(v) => updatePeriod(i, "open", v)} />
              <span className="text-gray-400 text-sm">→</span>
              <TimeInput value={period.close} onChange={(v) => updatePeriod(i, "close", v)} />
              {data.periods.length > 1 && (
                <button
                  onClick={() => removePeriod(i)}
                  className="rounded-lg p-1.5 text-red-400 hover:bg-red-50 dark:hover:bg-navy-700"
                >
                  <MdDelete className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addPeriod}
            className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-medium mt-1"
          >
            <MdAdd className="h-4 w-4" />
            Ajouter une période
          </button>
        </div>
      )}

      {!data.isOpen && (
        <p className="text-sm text-gray-400">Fermé</p>
      )}
    </div>
  );
}

export default function Schedule() {
  const { restaurantId } = useAuth();
  const [hours, setHours] = useState(defaultDays());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const today = DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]?.key;

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!restaurantId) return;
    getBusinessHours(restaurantId)
      .then((data) => {
        const raw = Array.isArray(data) ? data : data.data || [];
        if (raw.length) {
          const mapped = raw.reduce((acc, item) => {
            const dayIndex = Number(item.day);
            const dayKey = Number.isInteger(dayIndex) && dayIndex >= 0 && dayIndex <= 6
              ? DAY_KEYS[dayIndex]
              : item.day || item.name;
            const periods = item.periods || item.work_periods || [];

            if (!dayKey) return acc;

            acc[dayKey] = {
              isOpen: item.is_open ?? item.status ?? false,
              periods: periods.length
                ? periods.map((p) => ({
                    open: (p.open_time || p.open_at || p.open || "09:00").slice(0, 5),
                    close: (p.close_time || p.closed_at || p.close || "22:00").slice(0, 5),
                  }))
                : [emptyPeriod()],
            };
            return acc;
          }, {});
          setHours((prev) => ({ ...prev, ...mapped }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [restaurantId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = DAYS.map((d) => ({
        day: d.key,
        is_open: hours[d.key].isOpen,
        periods: hours[d.key].periods.map((p) => ({
          open_time: p.open,
          close_time: p.close,
        })),
      }));
      await updateBusinessHours(restaurantId, payload);
      showToast("Horaires enregistrés !");
    } catch {
      showToast("Erreur lors de l'enregistrement", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAllDays = () =>
    setHours(DAYS.reduce((acc, d) => ({ ...acc, [d.key]: { ...hours[d.key], isOpen: true } }), {}));

  const closeAllDays = () =>
    setHours(DAYS.reduce((acc, d) => ({ ...acc, [d.key]: { ...hours[d.key], isOpen: false } }), {}));

  const copyToAll = (sourceKey) => {
    const source = hours[sourceKey];
    setHours(DAYS.reduce((acc, d) => ({ ...acc, [d.key]: { ...source } }), {}));
    showToast(`Horaires de ${DAYS.find((d) => d.key === sourceKey)?.label} copiés à tous les jours`);
  };

  return (
    <div className="mt-3">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-lg ${
          toast.type === "error" ? "bg-red-500" : "bg-green-500"
        }`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
          <MdAccessTime className="h-7 w-7 text-brand-500" />
          Horaires d'ouverture
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button
            className="rounded-xl bg-green-500 px-3 py-2 text-xs font-semibold text-white hover:bg-green-600"
            onClick={openAllDays}
          >
            Ouvrir tous les jours
          </button>
          <button
            className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50"
            onClick={closeAllDays}
          >
            Fermer tous les jours
          </button>
          <button
            className="flex items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            <MdSave className="h-4 w-4" />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {DAYS.map((day) => (
            <div key={day.key}>
              <DayRow
                day={day}
                data={hours[day.key]}
                onChange={(updated) => setHours((prev) => ({ ...prev, [day.key]: updated }))}
                isToday={day.key === today}
              />
              {hours[day.key].isOpen && (
                <button
                  className="mt-1.5 w-full text-xs text-gray-400 hover:text-brand-500 text-center"
                  onClick={() => copyToAll(day.key)}
                >
                  Copier à tous les jours
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
